import { CheckboxAccordion } from 'components/shared/checkbox_accordion';
import { NumberField, SelectField, StringField } from 'components/shared/form/input';
import { ExAttributeValueForm } from 'entities/ex_attribute_value';
import { ExColumn } from 'entities/ex_column';
import { InstrumentForm, InstrumentOwnerableType } from 'entities/instrument';
import { ApiError, Form } from 'rac';
import React from 'react';

type Props = {
  form: Form<InstrumentForm>;
  apiError: ApiError;
  exColumns: ExColumn[];
  outsideLaboratoryName?: string | null;
};

/**
 * 製品管理情報の共通カラム、または選択中の顧客に紐づくカラムかを判定する。
 */
const isApplicableInstrumentInfoColumn = (
  column: ExColumn,
  clientableId?: number,
  clientableType?: InstrumentOwnerableType
) =>
  column.extensionClass === 'InstrumentInfoAttribute' &&
  ((!column.clientableId && !column.clientableType) ||
    (Number(column.clientableId) === Number(clientableId) && column.clientableType === clientableType));

/**
 * 製品管理情報のカラム定義に対応するフォーム値を構築する。
 * 既存の入力値をカラムID単位で維持しつつ、追加・削除されたカラムと表示順を最新の定義に同期する。
 */
export const buildInstrumentInfoAttributeValues = (
  exColumns: ExColumn[],
  currentValues: ExAttributeValueForm[],
  clientableId?: number,
  clientableType?: InstrumentOwnerableType
): ExAttributeValueForm[] => {
  const valuesByExColumnId = new Map(currentValues.map((value) => [value.exColumnId, value]));

  return exColumns
    .filter((column) => isApplicableInstrumentInfoColumn(column, clientableId, clientableType))
    .map((column) => valuesByExColumnId.get(column.id) || { exColumnId: column.id });
};

export const InstrumentInfoAttributeFields: React.FC<Props> = ({
  form,
  apiError,
  exColumns,
  outsideLaboratoryName,
}) => {
  const instrumentInfoColumns = exColumns.filter((column) =>
    isApplicableInstrumentInfoColumn(column, form.object.ownerableId, form.object.ownerableType)
  );
  const sharedColumns = instrumentInfoColumns.filter((column) => !column.clientableId && !column.clientableType);
  const clientColumns = instrumentInfoColumns.filter((column) => column.clientableId && column.clientableType);
  const sharedStandardColumns = sharedColumns.filter((column) => !column.stashed);
  const sharedDetailColumns = sharedColumns.filter((column) => column.stashed);
  const clientStandardColumns = clientColumns.filter((column) => !column.stashed);
  const clientDetailColumns = clientColumns.filter((column) => column.stashed);
  const columns = [...sharedStandardColumns, ...sharedDetailColumns, ...clientStandardColumns, ...clientDetailColumns];
  if (columns.length === 0) return null;

  return (
    <CheckboxAccordion
      key={`${form.object.ownerableType || ''}-${form.object.ownerableId || ''}`}
      label="機器の製品管理情報を設定する"
      showCheckbox={false}
      defaultChecked={columns.some((column) => column.required)}
    >
      <div
        style={{
          width: '100%',
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: 12,
        }}
      >
        {outsideLaboratoryName !== undefined && sharedColumns.length > 0 && (
          <div style={{ gridColumn: '1 / -1', fontSize: 18, fontWeight: 'bold', margin: '15px 0' }}>
            製品管理情報 - 共通
          </div>
        )}
        {columns.map((column, columnIndex) => {
          const index = (form.object.exAttributeValuesAttributes || []).findIndex(
            (value) => value.exColumnId === column.id
          );
          if (index < 0) return null;

          const common = {
            label: column.name,
            form,
            attr: ['exAttributeValuesAttributes', index, 'value'] as ['exAttributeValuesAttributes', number, 'value'],
            required: column.required,
            apiError,
          };

          const field = (() => {
            if (column.inputType === 'dropdown') {
              return (
                <SelectField
                  {...common}
                  labelId={`instrument-ex-column-${column.id}`}
                  data={(column.options || []).map((option) => ({ label: option, value: option }))}
                  includeBlank={!column.required}
                  formStyleProps={{ marginBottom: column.required ? 0 : 12 }}
                />
              );
            }
            if (column.inputType === 'integer' || column.inputType === 'price') {
              return <NumberField {...common} style={{ marginBottom: column.required ? 0 : 12 }} />;
            }
            return (
              <StringField
                {...common}
                multiline={column.inputType === 'text'}
                style={{ marginBottom: column.required ? 0 : 12 }}
              />
            );
          })();

          return (
            <React.Fragment key={column.id}>
              {sharedDetailColumns.length > 0 && columnIndex === sharedStandardColumns.length && (
                <label style={{ gridColumn: '1 / -1', marginBottom: 12 }}>詳細項目</label>
              )}
              {outsideLaboratoryName && clientColumns.length > 0 && columnIndex === sharedColumns.length && (
                <div style={{ gridColumn: '1 / -1', fontSize: 18, fontWeight: 'bold', margin: '15px 0' }}>
                  製品管理情報 - {outsideLaboratoryName}
                </div>
              )}
              {clientDetailColumns.length > 0 &&
                columnIndex === sharedColumns.length + clientStandardColumns.length && (
                  <label style={{ gridColumn: '1 / -1', marginBottom: 12 }}>詳細項目</label>
                )}
              <div style={{ minWidth: 0, gridColumn: column.inputType === 'text' ? '1 / -1' : undefined }}>{field}</div>
            </React.Fragment>
          );
        })}
      </div>
    </CheckboxAccordion>
  );
};
